import i18next, { type TOptions } from 'i18next';
import BACKEND from 'i18next-http-backend';
import ICU from 'i18next-icu';
import { ref } from 'vue';

import { persistLocaleCookie } from '@/utils/localeCookie';

const NAMESPACES = [
  'accessibility',
  'general',
  'language',
  'navigation',
  'login',
  'register',
  'reset_password',
  'mfa',
  'sso',
  'validation',
  'error',
  'data_jobs',
  'manage_network',
  'campaigns',
  'candidate_account',
  'profile',
  'onboarding'
];

const RTL_LOCALES = new Set(['ar-SA', 'ar', 'he-IL', 'he', 'fa-IR', 'fa', 'ur-PK', 'ur']);

function applyDocumentDirection(locale: string | undefined) {
  if (!locale) return;

  const lang = locale.split('-')[0];
  const isRtl = RTL_LOCALES.has(locale) || RTL_LOCALES.has(lang);
  document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
  document.documentElement.lang = locale;

  // font-family is handled by CSS rules scoped to html[lang] (see app.css)
}

// Reactive locale — used as a reactive dependency in templates and composables
const _locale = ref('en-US');

export const locale = _locale;

/**
 * Promesse d'init, mémorisée pour que changeLanguage() puisse l'attendre : le
 * garde du router (qui réaligne l'app sur la langue du compte) et le boot
 * i18next partent en parallèle dans app.ts, et un changement de langue demandé
 * sur une instance pas encore initialisée serait perdu.
 *
 * Créée ici, à l'import, et surtout pas dans initI18N : celui-ci n'est appelé
 * qu'une fois `static-data/locales.json` chargé (app.ts), donc plus tard que le
 * garde du router. Une promesse posée dans initI18N laisserait `changeLanguage`
 * attendre `null` et taper sur une instance i18next pas encore initialisée —
 * l'appel lève, i18next reste avec `language` à `undefined`, et l'init qui suit
 * résout dans cet état : plus de `app.mount()`, page blanche.
 */
let resolveInitialized: () => void;
const initialized = new Promise<void>(resolve => {
  resolveInitialized = resolve;
});

/**
 * `supportedLngs` vient de `static-data/locales.json` (voir composables/useLocales) :
 * la liste des langues n'est plus figée dans le front. `false` = aucune
 * restriction, filet de sécurité si le CDN n'a rien renvoyé.
 */
export function initI18N(
  translationUrl: string,
  locale = 'en-US',
  supportedLngs: string[] | false = false
): Promise<void> {
  i18next
    .use(ICU)
    .use(BACKEND)
    .init({
      ns: NAMESPACES,
      defaultNS: 'general',
      lng: locale,
      supportedLngs,
      load: 'currentOnly',
      fallbackLng: {
        'fr-FR': ['fr-FR'],
        'fr-CA': ['fr-FR'],
        fr: ['fr-FR'],
        default: ['en-US']
      },
      keySeparator: '.',
      debug: false,
      backend: {
        loadPath: `${translationUrl}/{{lng}}/{{ns}}.json`
      }
    })
    .then(() => {
      // `language` peut être absent si un changeLanguage a couru en parallèle :
      // on retombe alors sur la locale demandée plutôt que de casser le boot.
      const lng = i18next.language ?? locale;

      _locale.value = lng;
      applyDocumentDirection(lng);
      i18next.on('languageChanged', changed => {
        _locale.value = changed;
        applyDocumentDirection(changed);
      });
      resolveInitialized();
    });

  return initialized;
}

export const t = (key: string, options?: Omit<TOptions, 'context'>): string => {
  _locale.value; // reactive dependency — Vue re-renders when language changes
  const newKey = key.replace('.', ':');
  return options ? i18next.t(newKey, options) : i18next.t(newKey);
};

export async function changeLanguage(locale: string): Promise<void> {
  await initialized;
  await persistLocaleCookie(locale);
  await i18next.changeLanguage(locale);
}

export { i18next };
